import { inject, Injectable } from '@angular/core';

import { select, Store } from '@ngrx/store';
import { getContextParameterValues, isNotNullable, SiteContext, SiteContextConfig } from '@spartacus/core';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';

import { MEASUREMENT_SYSTEM_CONTEXT_ID } from '../constants/measurement-system-context-id.constant';
import { MeasurementSystemsActions } from '../store/actions/measurement-systems.action';
import { getActiveMeasurementSystem, getAllMeasurementSystems } from '../store/site-context.selectors';
import { MeasurementSystem } from '../types/measurement-system.type';

@Injectable({ providedIn: 'root' })
export class DpMeasurementSystemService implements SiteContext<MeasurementSystem> {
  readonly #store = inject(Store);
  readonly #config = inject(SiteContextConfig);

  getAll(): Observable<MeasurementSystem[]> {
    return this.#store.pipe(
      select(getAllMeasurementSystems),
      tap((measurementSystems) => {
        if (!measurementSystems) {
          this.#store.dispatch(MeasurementSystemsActions.loadMeasurementSystems());
        }
      }),
      filter(isNotNullable)
    );
  }

  getActive(): Observable<string> {
    return this.#store.pipe(select(getActiveMeasurementSystem), filter(isNotNullable));
  }

  getActiveLabel(): Observable<string> {
    return combineLatest([this.getAll(), this.getActive()]).pipe(
      map(([items, active]) => {
        const selected = (items ?? []).find((el) => el.code === active);
        return selected?.name ?? '';
      })
    );
  }

  setActive(code: string): void {
    this.#store.pipe(select(getActiveMeasurementSystem), take(1)).subscribe((activeMeasurementSystem) => {
      if (activeMeasurementSystem !== code && this.isValid(code)) {
        this.#store.dispatch(MeasurementSystemsActions.setActiveMeasurementSystem({ payload: code }));
      }
    });
  }

  isInitialized(): boolean {
    let valueInitialized = false;
    this.getActive()
      .subscribe(() => (valueInitialized = true))
      .unsubscribe();

    return valueInitialized;
  }

  protected isValid(value: string): boolean {
    return !!value && getContextParameterValues(this.#config, MEASUREMENT_SYSTEM_CONTEXT_ID).includes(value);
  }
}
